import { FC, useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { useLazyQuery } from '@apollo/client';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { baseVariables } from '@marriott/mi-ui-library';
import { getSuggestedPlaceDetailsQuery, SuggestedPlaceDetails } from '@marriott/mi-groups-graphql';
import { SearchFormData, SearchFormProps } from './SearchForm.types';
import { StyledSearchForm } from './SearchForm.styles';
import { SearchFormDesktop } from './SearchFormDesktop';
import { SearchFormMobile } from './SearchFormMobile';
import {
  useDttStore,
  useLocaleStore,
  useMediaQueryStore,
  useRfpTrayStore,
  useSearchFormStore,
  useSearchResultsStore,
} from '../../store';
import {
  getQueryParams,
  getSearchCriteria,
  getSearchQueryOptions,
  submitSearchForm,
  updateAriesGroupSearchSession,
  updateRecentSearches,
} from '../../utils';
import { useMediaQuery } from '../../hooks';
import { DEFAULT_LANG, SEARCH_TYPE, SEARCH_TYPE_VALUE } from '../../constants';

export const SearchFormConfig = {
  emptyLabel: 'SearchForm',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/groupsearch/searchresultsv2`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SearchForm: FC<{ model: SearchFormProps }> = (props: any) => {
  const { isSearchResultPage, searchResultsUrl, enabledSticky, styleclass } = props.model;

  // api call to get the details of suggested places
  const [getSuggestedPlaceDetails] = useLazyQuery(getSuggestedPlaceDetailsQuery);

  const { isNextGenSearchFormEnabled } = useDttStore();
  const { locale } = useLocaleStore();

  const hasNewSearchFormEnabled =
    isSearchResultPage && typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get(SEARCH_TYPE) === SEARCH_TYPE_VALUE
      : false;
  const { setLoader } = useSearchFormStore();
  const { isDesktop, setIsDesktop } = useMediaQueryStore();
  const { updateSearchQuery } = useSearchResultsStore();
  const { resetOnDestinationUpdate } = useRfpTrayStore();

  const matchesDesktop = useMediaQuery(baseVariables.mediaQuery.lg);

  const searchFormRef = useRef<HTMLDivElement | null>(null);

  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    if (matchesDesktop !== null) {
      setIsDesktop(matchesDesktop);
    }
  }, [matchesDesktop, setIsDesktop]);

  useEffect(() => {
    const navMenu = document.querySelector('.nav-menu');
    const searchResultsForm = searchFormRef?.current?.querySelector('.form-container');
    let scrollPosition = window.scrollY;

    document.body.classList.add('no-scroll');

    // to remove the added style when we stop the scroll inbetween & isSticky is false
    if (!isSticky) {
      navMenu?.removeAttribute('style');
      searchResultsForm?.removeAttribute('style');
    }

    const handleScroll = () => {
      if (!enabledSticky) return;
      if (searchFormRef.current) {
        if (isDesktop && isSticky && window.scrollY < scrollPosition) {
          navMenu?.setAttribute('style', 'position: fixed; top: 0; z-index: 60');
          searchResultsForm?.setAttribute('style', `top: ${navMenu?.clientHeight || 0}px`);
        } else {
          navMenu?.removeAttribute('style');
          searchResultsForm?.removeAttribute('style');
        }
        scrollPosition = window.scrollY;
        const offsetTop = searchFormRef.current.getBoundingClientRect().top + scrollPosition;
        setIsSticky(offsetTop <= scrollPosition);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledSticky, isSticky]);

  const submitSuccessCallback = () => {
    setLoader(false);
  };

  const handleSubmit = async (data: SearchFormData) => {
    let suggestedPlaceDetails = {} as SuggestedPlaceDetails;
    if (data.destination.placeId) {
      const response = await getPlaceDetails(data.destination.placeId);
      suggestedPlaceDetails = response?.suggestedPlaceDetails;
    }
    const queryData = updateRecentSearches(data, suggestedPlaceDetails);
    queryData[SEARCH_TYPE] = SEARCH_TYPE_VALUE;
    submitSearchForm(queryData, isSearchResultPage, searchResultsUrl, submitSuccessCallback);
    if (isSearchResultPage) {
      updateSearchQuery(getSearchQueryOptions(getQueryParams(window.location.search)), locale);
      resetOnDestinationUpdate(data.destination.description);
      updateAriesGroupSearchSession();
    }
    if (
      isSearchResultPage &&
      searchFormRef.current &&
      searchFormRef.current.getBoundingClientRect().top < window.scrollY
    ) {
      window.scrollTo(0, 0);
    }
  };

  const getPlaceDetails = async (placeId: string) => {
    try {
      const suggestedPlaceDetails = await getSuggestedPlaceDetails({
        variables: {
          placeId,
        },
        context: {
          headers: {
            'accept-language': locale,
          },
        },
        fetchPolicy: 'network-only',
      });
      return suggestedPlaceDetails.data;
    } catch (e) {
      return {};
    }
  };

  return (
    <EditableComponent config={SearchFormConfig} {...props}>
      {isNextGenSearchFormEnabled && locale === DEFAULT_LANG && (!isSearchResultPage || hasNewSearchFormEnabled) && (
        <StyledSearchForm
          data-testid="searchform"
          data-component-name="o-groups-searchform"
          ref={searchFormRef}
          className={styleclass}
        >
          {isDesktop !== null ? (
            <div
              className={clsx(
                'form-container',
                isSticky && 'my-5 pt-2 search-results-form sticky',
                !isSticky && isSearchResultPage && 'pt-2 search-results-form',
                !isSticky && !isSearchResultPage && 'search-form'
              )}
            >
              <div className="container px-0">
                {isDesktop ? (
                  <SearchFormDesktop
                    {...props.model}
                    isSticky={isSticky}
                    onSubmit={handleSubmit}
                    searchResultsFormData={getSearchCriteria(null)}
                  />
                ) : (
                  <SearchFormMobile
                    {...props.model}
                    isSticky={isSticky}
                    onSubmit={handleSubmit}
                    searchResultsFormData={getSearchCriteria(null)}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="search-form-loading my-4 skeleton-loader"></div>
          )}
        </StyledSearchForm>
      )}
    </EditableComponent>
  );
};
